import { getGlobalConfig } from '@core/globalConfig'
import type { ParamsConfig, ParamsType } from '@core/params'
import { debugParams, urlWidgetParams } from '@core/params'
import { parseQueryParams } from '@core/window'

import { parseParams } from './parseParams'

interface ParamsSource<Params> {
  defaultParams?: Partial<Params>
  fetchedParams?: Partial<Params> // Params set in naboo admin (fetched remotely)
  widgetAttributes?: Record<string, string> // Widget attributes
}

/**
 * Resolves params in particular order.
 * Note: Different from @core/params/resolveStoreParams.ts, it excludes player
 * specific params and works with generic params type. We should replace the
 * legacy resolveStoreParams with this one in the future.
 *
 * @param paramsSource The params source to resolve params from.
 * @param config The params config indicating which params are strings,
 *     booleans, and numbers.
 * @param silent Whether to suppress debug logs.
 * @returns The resolved params.
 */
export function resolveParams<Config extends ParamsConfig>(
  paramsSource: ParamsSource<ParamsType<typeof config>>,
  config: Config,
  silent = false,
) {
  const { defaultParams, fetchedParams, widgetAttributes } = paramsSource
  // Widget params specified via integration code as a html attributes
  const widgetParams = parseParams(widgetAttributes ?? {}, config)
  // Specified by a url query params `fwparam_{name}` likely to quickly override other params
  const urlParams = parseParams(urlWidgetParams(parseQueryParams()), config)
  // Specified by a `data-{name}` html attributes in a <script> tag to provide
  // global config params to all widgets on a page
  const globalConfigParams = parseParams(getGlobalConfig() ?? {}, config)

  const params: ParamsType<typeof config> = {
    ...defaultParams,
    ...fetchedParams,
    ...globalConfigParams,
    ...widgetParams,
    ...urlParams,
  }

  if (!silent) {
    debugParams(
      {
        defaultParams,
        fetchedParams,
        globalConfigParams,
        widgetParams,
        urlParams,
      },
      params,
    )
  }

  return params
}
